/**
 * Optimized Auth Context
 * Split from main AuthContext to reduce unnecessary re-renders
 */

import React, { createContext, useContext, useCallback, useMemo } from 'react';
import { User } from '@supabase/supabase-js';

// Core auth data that rarely changes
interface AuthData {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
}

// Auth actions that can be memoized
interface AuthActions {
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
  updateProfile: (_updates: unknown) => Promise<void>;
}

// Separate contexts for data and actions
const AuthDataContext = createContext<AuthData | undefined>(undefined);
const AuthActionsContext = createContext<AuthActions | undefined>(undefined);

interface OptimizedAuthProviderProps {
  children: React.ReactNode;
  user: User | null;
  loading: boolean;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
  updateProfile: (_updates: unknown) => Promise<void>;
}

export const OptimizedAuthProvider: React.FC<OptimizedAuthProviderProps> = ({
  children,
  user,
  loading,
  signOut,
  refreshSession,
  updateProfile,
}) => {
  // Memoize auth data to prevent unnecessary re-renders
  const authData = useMemo<AuthData>(
    () => ({
      user,
      loading,
      isAuthenticated: !!user,
    }),
    [user, loading],
  );

  // Memoize actions without calling hooks inside useMemo
  const signOutCb = useCallback(() => signOut(), [signOut]);
  const refreshSessionCb = useCallback(
    () => refreshSession(),
    [refreshSession],
  );
  const updateProfileCb = useCallback(
    (updates: unknown) => updateProfile(updates),
    [updateProfile],
  );

  const authActions = useMemo<AuthActions>(
    () => ({
      signOut: signOutCb,
      refreshSession: refreshSessionCb,
      updateProfile: updateProfileCb,
    }),
    [signOutCb, refreshSessionCb, updateProfileCb],
  );

  return (
    <AuthDataContext.Provider value={authData}>
      <AuthActionsContext.Provider value={authActions}>
        {children}
      </AuthActionsContext.Provider>
    </AuthDataContext.Provider>
  );
};

// Optimized hooks that only subscribe to what they need
export const useAuthData = () => {
  const context = useContext(AuthDataContext);
  if (context === undefined) {
    throw new Error('useAuthData must be used within an OptimizedAuthProvider');
  }
  return context;
};

export const useAuthActions = () => {
  const context = useContext(AuthActionsContext);
  if (context === undefined) {
    throw new Error(
      'useAuthActions must be used within an OptimizedAuthProvider',
    );
  }
  return context;
};

// Convenience hook for components that need both
export const useOptimizedAuth = () => {
  const authData = useAuthData();
  const authActions = useAuthActions();

  return {
    ...authData,
    ...authActions,
  };
};

// Selector hooks for specific data
export const useUser = () => useAuthData().user;
export const useIsAuthenticated = () => useAuthData().isAuthenticated;
export const useAuthLoading = () => useAuthData().loading;
